import type { ComponentChildren, VNode } from 'preact';
import { cloneElement, isValidElement } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Portal } from './Portal';
import type { Placement } from './Popover';
import { cn } from '@/lib/cn';

export interface HoverCardProps {
  content: ComponentChildren;
  placement?: Placement;
  delay?: number;
  className?: string;
  children: VNode;
}

export function HoverCard({
  content,
  placement = 'bottom-start',
  delay = 250,
  className,
  children,
}: HoverCardProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 100);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const t = triggerRef.current;
    const card = cardRef.current;
    if (!t || !card) return;
    const a = t.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    const offset = 8;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let isTop = placement.startsWith('top');
    // Auto-flip vertical: si no cabe arriba y sí cabe abajo (o viceversa).
    if (isTop && a.top - c.height - offset < margin && a.bottom + c.height + offset + margin <= vh) {
      isTop = false;
    } else if (!isTop && a.bottom + c.height + offset > vh - margin && a.top - c.height - offset - margin >= 0) {
      isTop = true;
    }
    const top = isTop ? a.top - c.height - offset : a.bottom + offset;
    let left = a.left;
    if (placement.endsWith('end')) left = a.right - c.width;
    else if (!placement.includes('-')) left = a.left + (a.width - c.width) / 2;
    // Clamp horizontal al viewport con margen.
    left = Math.max(margin, Math.min(left, vw - c.width - margin));
    setPos({ top: top + window.scrollY, left: left + window.scrollX });
  }, [open, placement]);

  if (!isValidElement(children)) return children;

  const trigger = cloneElement(children, {
    ref: triggerRef,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
  });

  return (
    <>
      {trigger}
      {open ? (
        <Portal layer="popover">
          <div
            ref={cardRef}
            role="dialog"
            onMouseEnter={() => timer.current && clearTimeout(timer.current)}
            onMouseLeave={hide}
            className={cn(
              'fixed w-[280px] rounded-md border border-border bg-surface p-3 shadow-lg animate-fade-in text-13 text-fg',
              className,
            )}
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>
        </Portal>
      ) : null}
    </>
  );
}
